import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { TrainerAPI } from "../api/TrainerAPI";
import quizData from "../data/quizData";
import {
  PlayCircle,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  RotateCcw,
  Trophy,
  AlertTriangle,
} from "lucide-react";
import logo from "../assets/think_cloudly_logo_transparent.png";

const PASSING_SCORE = 75;

const OnboardingPage = () => {
  const { setOnboardingDone } = useAuth();

  // Phases: "video" | "quiz" | "result"
  const [phase, setPhase] = useState("video");
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  // S3 Video states
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);

  const videoRef = useRef(null);

  // Fetch S3 signed video URL on mount
  useEffect(() => {
    setVideoLoading(true);
    TrainerAPI.getOnboardingVideoUrl()
      .then((url) => {
        setVideoUrl(url);
        setVideoLoading(false);
      })
      .catch(() => {
        setVideoError(true);
        setVideoLoading(false);
      });
  }, []);

  // Handle HTML5 video end
  const handleVideoEnded = () => {
    setVideoCompleted(true);
  };

  // Select an answer
  const handleSelectAnswer = (questionIndex, optionIndex) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionIndex]: optionIndex,
    }));
  };

  // Submit quiz
  const handleSubmitQuiz = async () => {
    if (Object.keys(selectedAnswers).length < quizData.length) return;

    setSubmitting(true);
    try {
      const answers = quizData.map((_, idx) => selectedAnswers[idx]);
      const result = await TrainerAPI.submitQuiz(answers);
      setQuizResult(result);
      setPhase("result");

      if (result.passed) {
        setShowConfetti(true);
        setTimeout(() => {
          setOnboardingDone();
        }, 3000);
      }
    } catch (err) {
      console.error("Quiz submission error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  // Retry — go back to video
  const handleRetry = () => {
    setPhase("video");
    setVideoCompleted(false);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setQuizResult(null);
    setShowConfetti(false);
  };

  const allAnswered = Object.keys(selectedAnswers).length === quizData.length;
  const progress = ((currentQuestion + 1) / quizData.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e] flex items-center justify-center p-2 sm:p-4 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-60 h-60 bg-[#FC7A69]/20 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-10 right-10 w-60 h-60 bg-[#221E45]/40 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-1/2 left-1/3 w-40 h-40 bg-purple-500/10 rounded-full blur-2xl animate-pulse"
          style={{ animationDelay: "2s" }}
        />
      </div>

      {/* Confetti Effect */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="confetti-piece"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                backgroundColor: [
                  "#FC7A69",
                  "#221E45",
                  "#FFD700",
                  "#00D4AA",
                  "#FF6B9D",
                  "#7C5CFC",
                ][i % 6],
              }}
            />
          ))}
        </div>
      )}

      <div className="relative z-10 w-full max-w-2xl">
        {/* Logo & Header */}
        <div className="text-center mb-3">
          <img
            src={logo}
            alt="ThinkCloudly"
            className="w-10 h-10 mx-auto mb-2 drop-shadow-lg"
            draggable={false}
          />
          <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
            Welcome to ThinkCloudly
          </h1>
          <p className="text-gray-300 mt-1 text-xs">
            {phase === "video" && "Watch the onboarding video to get started"}
            {phase === "quiz" && "Answer the quiz questions to proceed"}
            {phase === "result" &&
              (quizResult?.passed ? "You're all set!" : "Let's try again")}
          </p>
        </div>

        {/* ==================== VIDEO PHASE ==================== */}
        {phase === "video" && (
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden animate-fadeIn">
            {/* Video Container */}
            <div
              className="relative bg-black rounded-t-2xl overflow-hidden"
              style={{ aspectRatio: "16/9" }}
            >
              {/* Loading State */}
              {videoLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  <div className="w-10 h-10 border-2 border-white/20 border-t-[#FC7A69] rounded-full animate-spin" />
                  <span className="text-white/50 text-sm">
                    Loading video...
                  </span>
                </div>
              )}

              {/* Error State */}
              {videoError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  <AlertTriangle className="w-10 h-10 text-red-400" />
                  <span className="text-red-300 text-sm">
                    Failed to load video. Please refresh the page.
                  </span>
                </div>
              )}

              {/* Video Player */}
              {videoUrl && !videoError && (
                <video
                  ref={videoRef}
                  src={videoUrl}
                  onEnded={handleVideoEnded}
                  className="w-full h-full"
                  controls
                  controlsList="nodownload"
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                />
              )}

              {/* Watch complete overlay — only shown before completion */}
              {!videoCompleted && videoUrl && !videoError && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pointer-events-none">
                  <div className="flex items-center gap-2 text-white/70 text-sm">
                    <PlayCircle className="w-4 h-4" />
                    <span>Please watch the complete video to proceed</span>
                  </div>
                </div>
              )}
            </div>

            {/* Action Area */}
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-3 rounded-full ${videoCompleted ? "bg-green-400 shadow-lg shadow-green-400/50" : "bg-gray-500"}`}
                />
                <span
                  className={`text-sm font-medium ${videoCompleted ? "text-green-300" : "text-gray-400"}`}
                >
                  {videoCompleted
                    ? "Video completed ✓"
                    : "Video in progress..."}
                </span>
              </div>

              {/* Dev skip — remove in production */}
              {/* <button
                onClick={() => setVideoCompleted(true)}
                className="text-xs text-gray-500 hover:text-gray-300 underline transition-colors"
              >
                Skip (dev only)
              </button> */}

              <button
                onClick={() => setPhase("quiz")}
                disabled={!videoCompleted}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
                  videoCompleted
                    ? "bg-gradient-to-r from-[#FC7A69] to-[#e45c53] text-white shadow-lg shadow-[#FC7A69]/30 hover:shadow-xl hover:shadow-[#FC7A69]/40 hover:scale-105"
                    : "bg-gray-700 text-gray-500 cursor-not-allowed"
                }`}
              >
                Continue to Quiz
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ==================== QUIZ PHASE ==================== */}
        {phase === "quiz" && (
          <div className="animate-fadeIn">
            {/* Progress Bar */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-300">
                  Question {currentQuestion + 1} of {quizData.length}
                </span>
                <span className="text-xs text-gray-400">
                  {Object.keys(selectedAnswers).length}/{quizData.length}{" "}
                  answered
                </span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#FC7A69] to-[#e45c53] rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Question Card */}
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-4 sm:p-6">
              <div className="mb-3">
                <span className="inline-block px-2 py-0.5 bg-[#FC7A69]/20 text-[#FC7A69] rounded-full text-[10px] font-semibold mb-2">
                  Question {currentQuestion + 1}
                </span>
                <h2 className="text-base sm:text-lg font-bold text-white leading-snug">
                  {quizData[currentQuestion].question}
                </h2>
              </div>

              {/* Options */}
              <div className="space-y-2 mb-4">
                {quizData[currentQuestion].options.map((option, idx) => {
                  const isSelected = selectedAnswers[currentQuestion] === idx;
                  return (
                    <button
                      key={idx}
                      onClick={() => handleSelectAnswer(currentQuestion, idx)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all duration-200 group ${
                        isSelected
                          ? "border-[#FC7A69] bg-[#FC7A69]/15 text-white"
                          : "border-white/10 bg-white/5 text-gray-300 hover:border-white/30 hover:bg-white/10"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all ${
                            isSelected
                              ? "bg-[#FC7A69] text-white"
                              : "bg-white/10 text-gray-400 group-hover:bg-white/20"
                          }`}
                        >
                          {String.fromCharCode(65 + idx)}
                        </div>
                        <span className="text-xs sm:text-sm font-medium">
                          {option}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <button
                  onClick={() =>
                    setCurrentQuestion((prev) => Math.max(0, prev - 1))
                  }
                  disabled={currentQuestion === 0}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    currentQuestion === 0
                      ? "text-gray-600 cursor-not-allowed"
                      : "text-gray-300 hover:text-white hover:bg-white/10"
                  }`}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Previous
                </button>

                {/* Question dots */}
                <div className="hidden sm:flex gap-1.5">
                  {quizData.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentQuestion(idx)}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        idx === currentQuestion
                          ? "bg-[#FC7A69] scale-125"
                          : selectedAnswers[idx] !== undefined
                            ? "bg-green-400"
                            : "bg-white/20 hover:bg-white/40"
                      }`}
                    />
                  ))}
                </div>

                {currentQuestion < quizData.length - 1 ? (
                  <button
                    onClick={() => setCurrentQuestion((prev) => prev + 1)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-all"
                  >
                    Next
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmitQuiz}
                    disabled={!allAnswered || submitting}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all duration-300 ${
                      allAnswered && !submitting
                        ? "bg-gradient-to-r from-[#FC7A69] to-[#e45c53] text-white shadow-lg shadow-[#FC7A69]/30 hover:shadow-xl hover:scale-105"
                        : "bg-gray-700 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Quiz
                        <CheckCircle className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ==================== RESULT PHASE ==================== */}
        {phase === "result" && quizResult && (
          <div className="animate-fadeIn">
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-6 sm:p-8 text-center">
              {quizResult.passed ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center animate-bounce-slow">
                    <Trophy className="w-8 h-8 text-yellow-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Congratulations! 🎉
                  </h2>
                  <p className="text-green-300 text-lg mb-6">
                    You scored {quizResult.score}% — You passed!
                  </p>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6 inline-block">
                    <p className="text-green-200 text-sm">
                      <strong>{quizResult.correctCount}</strong> out of{" "}
                      <strong>{quizResult.total}</strong> correct
                    </p>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Redirecting to your dashboard...
                  </p>
                  <div className="mt-4 flex justify-center">
                    <div className="w-8 h-8 border-2 border-white/20 border-t-[#FC7A69] rounded-full animate-spin" />
                  </div>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-8 h-8 text-red-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Almost There!
                  </h2>
                  <p className="text-red-300 text-lg mb-6">
                    You scored {quizResult.score}% — You need at least{" "}
                    {PASSING_SCORE}% to pass
                  </p>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-8 inline-block">
                    <p className="text-red-200 text-sm">
                      <strong>{quizResult.correctCount}</strong> out of{" "}
                      <strong>{quizResult.total}</strong> correct
                    </p>
                  </div>
                  <div>
                    <button
                      onClick={handleRetry}
                      className="flex items-center gap-2 mx-auto px-8 py-4 bg-gradient-to-r from-[#FC7A69] to-[#e45c53] text-white rounded-xl font-bold text-sm shadow-lg shadow-[#FC7A69]/30 hover:shadow-xl hover:scale-105 transition-all duration-300"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Watch Video & Try Again
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Inline Styles */}
      <style>{`
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-bounce-slow {
          animation: bounceSlow 2s ease-in-out infinite;
        }
        @keyframes bounceSlow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .confetti-piece {
          position: fixed;
          width: 10px;
          height: 10px;
          top: -10px;
          border-radius: 2px;
          animation: confettiFall 3s ease-in-out forwards;
        }
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default OnboardingPage;
